import { AutoComplete, Button, Icons, Input, Tag, Typography } from '@pankod/refine-antd';
import { useSearchParams } from '@pankod/refine-react-router-v6';
import { CustomDatePicker } from 'components/date-picker';
import { useDebounce } from 'hooks';
import React, { useEffect, useState } from 'react';
import { getSearchData } from 'services/meilisearch';
import { capitalize, getDateRangeOfLastXMonths } from 'utilities/shared';

const { Text } = Typography;

interface IGroupOption {
  value: string;
  label: string | React.ReactNode;
}

interface IOption {
  label: string | React.ReactNode;
  options: IGroupOption[];
}

export type Group =
  | 'id'
  | 'client'
  | 'supplier'
  | 'user'
  | 'warehouse'
  | 'product'
  | 'tag'
  | 'category'
  | 'sku'
  | 'name'
  | 'clientCode'
  | 'supplierCode'
  | 'email'
  | 'mobileNumber'
  | 'warehouseCode'
  | 'area'
  | 'material';

type SearchProps = {
  placeholder: string;
  className?: string;
  style?: React.CSSProperties;
  size: 'small' | 'middle' | 'large';
  index: string;
  groups?: Group[];
  dateRange?: [Date, Date];
  onSearch: (query: {
    startDate?: string;
    endDate?: string;
    searchString: string;
  }) => Promise<void>;
};

const renderTitle = (title: string) => (
  <Text strong style={{ fontSize: '1rem' }} key={title}>{`${title}`}</Text>
);

const renderItem = (key: string, title: string, groupName: string) => ({
  value: `${groupName}::${title}`,
  label: <Text key={key}>{title}</Text>,
});

const getGroupOptions = (name: string, options: string[]) => {
  const groupOptions: IGroupOption[] = [];

  for (let [idx, value] of options.entries()) {
    if (!value) {
      continue;
    }
    groupOptions.push(renderItem(`${value}-${idx}`, value, name));
  }

  return groupOptions;
};

const getGroupsOptions = async (index: string, searchText: string) => {
  // Get search values from meilisearch
  const searchValues = await getSearchData(index, searchText);

  // Insert meilisearch result into their specific arrays
  const ids: string[] = [];
  const clients: string[] = [];
  const suppliers: string[] = [];
  const users: string[] = [];
  const warehouses: string[] = [];
  const products: string[] = [];
  const tags: string[] = [];
  const categories: string[] = [];
  const names: string[] = [];
  const skus: string[] = [];
  const clientCodes: string[] = [];
  const supplierCodes: string[] = [];
  const emails: string[] = [];
  const mobileNumbers: string[] = [];
  const areas: string[] = [];
  const warehouseCodes: string[] = [];
  const materials: string[] = [];

  searchValues.forEach((item) => {
    item.id && ids.push(item.id);
    item.client && clients.push(!clients.includes(item.client) && item.client);
    item.supplier && suppliers.push(!suppliers.includes(item.supplier) && item.supplier);
    item.user && users.push(!users.includes(item.user) && item.user);
    item.name && names.push(!names.includes(item.name) && item.name);
    item.sku && skus.push(!skus.includes(item.sku) && item.sku);
    item.clientCode && clientCodes.push(!clientCodes.includes(item.clientCode) && item.clientCode);
    item.supplierCode &&
      supplierCodes.push(!supplierCodes.includes(item.supplierCode) && item.supplierCode);
    item.email && emails.push(!emails.includes(item.email) && item.email);
    item.mobileNumber &&
      mobileNumbers.push(!mobileNumbers.includes(item.mobileNumber) && item.mobileNumber);

    item.area && areas.push(!areas.includes(item.area) && item.area);
    item.warehouseCode &&
      warehouseCodes.push(!warehouseCodes.includes(item.warehouseCode) && item.warehouseCode);
    item.product && products.push(!products.includes(item.product) && item.product);

    item.warehouse &&
      !Array.isArray(item.warehouse) &&
      warehouses.push(!warehouses.includes(item.warehouse) && item.warehouse);

    if (Array.isArray(item.warehouse)) {
      item.warehouse.forEach((warehouse: string) => {
        if (!warehouses.includes(warehouse)) {
          warehouses.push(warehouse);
        }
      });
    }

    if (Array.isArray(item.products)) {
      item.products.forEach((product: string) => {
        if (!products.includes(product)) {
          products.push(product);
        }
      });
    }
    if (Array.isArray(item.tags)) {
      item.tags.forEach((tag: string) => {
        if (!tags.includes(tag)) {
          tags.push(tag);
        }
      });
    }
    if (Array.isArray(item.categories)) {
      item.categories.forEach((category: string) => {
        if (!categories.includes(category)) {
          categories.push(category);
        }
      });
    }
    if (Array.isArray(item.materials)) {
      item.materials.forEach((material: string) => {
        if (!materials.includes(material)) {
          materials.push(material);
        }
      });
    }
  });

  // Create option group object
  const groupsOptions: Record<string, IGroupOption[]> = {
    id: getGroupOptions('id', ids),
    client: getGroupOptions('client', clients),
    supplier: getGroupOptions('supplier', suppliers),
    user: getGroupOptions('user', users),
    warehouse: getGroupOptions('warehouse', warehouses),
    product: getGroupOptions('product', products),
    tag: getGroupOptions('tag', tags),
    name: getGroupOptions('name', names),
    sku: getGroupOptions('sku', skus),
    category: getGroupOptions('category', categories),
    clientCode: getGroupOptions('clientCode', clientCodes),
    supplierCode: getGroupOptions('supplierCode', supplierCodes),
    email: getGroupOptions('email', emails),
    mobileNumber: getGroupOptions('mobileNumber', mobileNumbers),
    area: getGroupOptions('area', areas),
    warehouseCode: getGroupOptions('warehouseCode', warehouseCodes),
    material: getGroupOptions('material', materials),
  };

  return groupsOptions;
};

export const SearchWithDateRange: React.FC<SearchProps> = (props) => {
  const [searchText, setSearchText] = useState<string>();
  const [tags, setTags] = useState<string[]>([]);
  const [options, setOptions] = useState<IOption[]>([]);
  const [dateRange, setDateRange] = useState<[Date, Date]>(
    props.dateRange ?? getDateRangeOfLastXMonths(3)
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const clientSearchParam = searchParams.get('client') ?? '';

  const debouncedSearchText = useDebounce(searchText, 500);
  useEffect(() => {
    (async () => {
      if (debouncedSearchText) {
        const groupsOptions = await getGroupsOptions(props.index, debouncedSearchText);
        const options: IOption[] = [];
        if (!props.groups || (Array.isArray(props.groups) && !props.groups.length)) {
          for (const key in groupsOptions) {
            options.push({
              label: renderTitle(capitalize(key)),
              options: groupsOptions[key],
            });
          }
        }
        if (Array.isArray(props.groups) && props.groups.length) {
          for (const value of props.groups) {
            options.push({
              label: renderTitle(capitalize(value)),
              options: groupsOptions[value],
            });
          }
        }

        setOptions(options);
        return;
      }

      setOptions([]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchText]);

  useEffect(() => {
    if (clientSearchParam) {
      handleSelect(`client::${clientSearchParam}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSearchParam]);

  const handleDateChange = async (values: any) => {
    if (values && values[0] && values[1]) {
      const startDate = new Date(values[0]);
      const endDate = new Date(values[1]);

      await props.onSearch({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        searchString: tags.join(', '),
      });
      setDateRange([startDate, endDate]);
    }
  };

  const handleSelect = async (selection: string) => {
    setSearchText('');

    const [groupName, option] = selection.split('::');
    if (!tags.includes(option)) {
      const newTags = [...tags, option];
      setTags(newTags);

      if (groupName === 'id') {
        await props.onSearch({ searchString: newTags.join(', ') });
      } else {
        await props.onSearch({
          startDate: dateRange[0].toISOString(),
          endDate: dateRange[1].toISOString(),
          searchString: newTags.join(', '),
        });
      }
    }
  };

  const handleCloseTag = async (removedTag: string) => {
    const filteredTags = tags.filter((tag) => tag !== removedTag);
    setTags(filteredTags);
    await props.onSearch({
      startDate: dateRange[0].toISOString(),
      endDate: dateRange[1].toISOString(),
      searchString: filteredTags.join(', '),
    });
    searchParams.delete('client');
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
    });
  };

  const onResetClick = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setTags([]);
    await props.onSearch({
      startDate: dateRange[0].toISOString(),
      endDate: dateRange[1].toISOString(),
      searchString: '',
    });
  };

  const renderedTags = tags.map((tag, index) => {
    return (
      <Tag
        key={index}
        color="blue"
        closable
        onClose={(e) => {
          e.preventDefault();
          handleCloseTag(tag);
        }}
      >
        {tag}
      </Tag>
    );
  });

  return (
    <section
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '1rem',
      }}
    >
      <CustomDatePicker
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          maxWidth: '400px',
        }}
        value={dateRange}
        onChange={handleDateChange}
      />

      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <div className={props.className} style={{ width: '100%' }}>
          <AutoComplete
            style={{ width: '100%' }}
            value={searchText}
            options={options}
            onChange={(value) => setSearchText(value)}
            onSelect={handleSelect}
            suffixIcon={<Icons.ReloadOutlined />}
          >
            <Input
              size={props.size}
              placeholder={props.placeholder}
              suffix={<Icons.SearchOutlined />}
            />
          </AutoComplete>
          {!!tags.length && (
            <Button
              size={props.size}
              onClick={onResetClick}
              icon={<Icons.ReloadOutlined />}
              style={{ marginLeft: '0.5rem' }}
            />
          )}
        </div>

        <div style={{ marginTop: '1rem' }}>{renderedTags}</div>
      </div>
    </section>
  );
};
